/* eslint-disable no-restricted-syntax */
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import tabs from '@/components/generics/tabs/tabs.vue';
import cmsContentMixin from '@/mixins/cmsContent';
import contentLoaderMixin from '@/mixins/contentLoader';
import { flatten, get } from 'lodash';
import { stripTags } from '@/utils/helpers';

const STORY_SWITCH_INTERVAL = 8 * 1000;
const CONTENT_COLLAPSED_HEIGHT = 475;
const CONTENT_OVERFLOW_TOLERANCE = 12;
const EXPANDED_HEIGHT_BUFFER = 8;
const EXPANDED_HEIGHT_MEASURE_DELAYS = [0, 150, 500, 1200];
const PODCAST_RESIZE_EVENT = 'cms:podcast-resize';
const PODCAST_LAYOUT_REFRESH_EVENT = 'cms:podcast-layout-refresh';
const LAST_STORY_ROW_CLASS = 'preview-carousel__story-row--last';
let interval = null;

export default {
  mixins: [
    cmsContentMixin,
    contentLoaderMixin,
  ],
  components: {
    tabs,
  },
  data() {
    return {
      stripTags,
      isLoading: false,
      selected: {},
      selectedManually: false,
      isTransitioning: false,
      storyContentMap: {},
      activeTab: null,
      showNavigationArrows: false,
      canScrollLeft: false,
      canScrollRight: false,
      isExpanded: false,
      isContentOverflowing: false,
      expandedContentHeight: null,
      contentMeasureTimeouts: [],
      contentResizeObserver: null,
      refreshIndex: 0,
    };
  },
  computed: {
    ...mapGetters('app', ['isLayoutMobile', 'isLayoutDesktop']),
    ...mapState('browse', ['storiesNav', 'storyMap', 'currentStoryIndex']),
    ...mapState('popup', ['currentPopup', 'currentSmallPopup']),
    showNav() {
      return this.storiesNav.length > 0 && !this.isLoading && this.isComponentReady;
    },
    navigationTabs() {
      return this.storiesNav.map((item) => {
        return {
          name: `story-${item.index}`,
          title: item.navTitle || `Story #${item.index + 1}`,
        }
      })
    },
    classModifiers() {
      return {
        'preview-carousel': true,
      };
    },
    contentClassModifiers() {
      return {
        'preview-carousel__content': true,
        'preview-carousel__content--collapsed': !this.isExpanded,
        'preview-carousel__content--overflowing': this.isContentOverflowing && !this.isExpanded,
      };
    },
    contentStyleModifiers() {
      const minHeightPx = CONTENT_COLLAPSED_HEIGHT;
      if (this.isExpanded && this.expandedContentHeight) {
        return {
          minHeight: `${Math.max(minHeightPx, this.expandedContentHeight)}px`,
        };
      }
      return {
        minHeight: `${minHeightPx}px`,
      };
    },
    expandClassModifiers() {
      return {
        'preview-carousel__expand': true,
        'preview-carousel__expand--floating': !this.isExpanded,
      };
    },
    showExpandControl() {
      return this.isContentOverflowing && this.isComponentReady;
    },
    tabsClassModifiers() {
      return {
        'preview-carousel__tabs': true,
        'preview-carousel__tabs--arrows': this.showNavigationArrows
      }
    },
  },
  methods: {
    ...mapActions('browse', ['getStoriesNav', 'getStory', 'getStoriesList', 'updateStoryMapFromContractNotification']),
    ...mapActions('popup', ['setCurrentPopup']),
    getStoryContentForLayout(data) {
      return this.isLayoutDesktop ? data.desktopContent : data.mobileContent;
    },
    clearPendingExpandedMeasurements() {
      for (const timeoutId of this.contentMeasureTimeouts) {
        clearTimeout(timeoutId);
      }
      this.contentMeasureTimeouts = [];
    },
    teardownContentResizeObserver() {
      if (this.contentResizeObserver) {
        this.contentResizeObserver.disconnect();
        this.contentResizeObserver = null;
      }
    },
    setupContentResizeObserver() {
      this.teardownContentResizeObserver();
      if (typeof ResizeObserver === 'undefined') {
        return;
      }

      const target = this.getActiveStoryElement() || this.$refs.content;
      if (!target) {
        return;
      }

      this.contentResizeObserver = new ResizeObserver(() => {
        if (this.isExpanded) {
          this.scheduleExpandedHeightRefresh();
        } else {
          this.checkContentOverflow();
        }
      });
      this.contentResizeObserver.observe(target);
    },
    getActiveStoryElement() {
      const content = this.$refs.content;
      if (!content) {
        return null;
      }
      const stories = content.querySelectorAll('.preview-carousel__story');
      for (const story of stories) {
        if (window.getComputedStyle(story).display !== 'none') {
          return story;
        }
      }
      return null;
    },
    isVisibleElement(node) {
      const styles = window.getComputedStyle(node);
      if (styles.display === 'none' || styles.visibility === 'hidden') {
        return false;
      }
      const rect = node.getBoundingClientRect();
      return rect.width > 0 || rect.height > 0 || node.scrollHeight > 0;
    },
    getLastVisibleStoryRow(activeStory = this.getActiveStoryElement()) {
      if (!activeStory) {
        return null;
      }

      const rows = activeStory.querySelectorAll('.browse-story-content-region__items');
      for (let index = rows.length - 1; index >= 0; index -= 1) {
        const row = rows[index];
        if (this.isVisibleElement(row)) {
          return row;
        }
      }

      return null;
    },
    updateLastVisibleStoryRow(activeStory = this.getActiveStoryElement()) {
      if (!activeStory) {
        return;
      }

      const markedRows = activeStory.querySelectorAll(`.${LAST_STORY_ROW_CLASS}`);
      for (const row of markedRows) {
        row.classList.remove(LAST_STORY_ROW_CLASS);
      }

      const lastVisibleRow = this.getLastVisibleStoryRow(activeStory);
      if (lastVisibleRow) {
        lastVisibleRow.classList.add(LAST_STORY_ROW_CLASS);
      }
    },
    measureStoryHeight({ includeBuffer = false } = {}) {
      const activeStory = this.getActiveStoryElement();
      if (!activeStory) {
        return null;
      }

      this.updateLastVisibleStoryRow(activeStory);

      const activeRect = activeStory.getBoundingClientRect();
      let measuredHeight = Math.max(activeStory.scrollHeight || 0, activeStory.offsetHeight || 0);
      const descendants = activeStory.querySelectorAll('*');

      for (const node of descendants) {
        const styles = window.getComputedStyle(node);
        if (styles.display === 'none' || styles.visibility === 'hidden') {
          continue;
        }
        const rect = node.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) {
          continue;
        }
        const nodeBottom = rect.bottom - activeRect.top;
        if (nodeBottom > measuredHeight) {
          measuredHeight = nodeBottom;
        }
      }

      if (measuredHeight <= 0) {
        return null;
      }

      return Math.ceil(measuredHeight + (includeBuffer ? EXPANDED_HEIGHT_BUFFER : 0));
    },
    measureExpandedContentHeight() {
      return this.measureStoryHeight({ includeBuffer: true });
    },
    measureCollapsedContentHeight() {
      return this.measureStoryHeight();
    },
    scheduleExpandedHeightRefresh() {
      if (!this.isExpanded) {
        return;
      }
      this.clearPendingExpandedMeasurements();
      for (const delay of EXPANDED_HEIGHT_MEASURE_DELAYS) {
        const timeoutId = setTimeout(() => {
          if (!this.isExpanded) {
            return;
          }
          const measuredHeight = this.measureExpandedContentHeight();
          if (measuredHeight) {
            this.expandedContentHeight = measuredHeight;
          }
        }, delay);
        this.contentMeasureTimeouts.push(timeoutId);
      }
    },
    handleWindowResize() {
      this.checkOverflow();
      if (this.isExpanded) {
        this.scheduleExpandedHeightRefresh();
      } else {
        this.checkContentOverflow();
      }
    },
    handleEmbeddedResizeEvent() {
      if (this.isExpanded) {
        this.scheduleExpandedHeightRefresh();
      } else {
        this.$nextTick(() => {
          this.checkContentOverflow();
        });
      }
    },
    emitPodcastLayoutRefresh() {
      window.dispatchEvent(new Event(PODCAST_LAYOUT_REFRESH_EVENT));
    },
    tabsClicked() {
      this.stopNavRotation();
      this.selectedManually = true;
    },
    navigationTabChange(name) {
      this.activeTab = name;
      const index = parseInt(name.replace('story-', ''), 10);
      this.onSelect({ item: this.storiesNav.find(i => i.index === index), stop: false });
    },
    installActiveStoryCharts() {
      const activeStoryElement = this.getActiveStoryElement();
      if (!activeStoryElement) {
        return;
      }
      this.installMarketCharts(activeStoryElement, { suppressGlobalLoader: true });
    },
    setupContent() {
      const totalCmsContent = [];
      this.storyContentMap = {};

      for (const [index, data] of Object.entries(this.storyMap)) {
        const storyContent = this.getStoryContentForLayout(data);
        totalCmsContent.push(storyContent.cmsContent || {});
        Vue.set(this.storyContentMap, index, `${storyContent.css || ''}${storyContent.html || ''}`);
      }
      const accordionContentObjects = flatten(totalCmsContent.map((i) => i.accordionContentObjects || []));
      const podcastItemContentObjects = flatten(totalCmsContent.map((i) => i.podcastItemContentObjects || []));
      const contractListMarkets = flatten(totalCmsContent.map((i) => i.contractListMarkets || []));
      const marketTileMarkets = flatten(totalCmsContent.map((i) => i.marketTileMarkets || []));
      const chartWidgetObjects = flatten(totalCmsContent.map((i) => i.chartWidgetObjects || []));
      const docsWidgetObjects = flatten(totalCmsContent.map((i) => i.docsWidgetObjects || []));
      const videoWidgetObjects = flatten(totalCmsContent.map((i) => i.videoWidgetObjects || []));
      this.isComponentReady = true;

      setTimeout(() => {
        this.installDynamicControls({
          data: {
            cmsContentObject: {
              accordionContentObjects,
              podcastItemContentObjects,
              contractListMarkets,
              marketTileMarkets,
              chartWidgetObjects,
              docsWidgetObjects,
              videoWidgetObjects
            }
          },
          skipMarketCharts: true,
        });
        this.$nextTick(() => {
          this.installActiveStoryCharts();
          this.setupContentResizeObserver();
          this.checkContentOverflow();
          this.emitPodcastLayoutRefresh();
        });
      }, 0)
    },
    refreshLayoutContent({ clearExpanded = true } = {}) {
      if (!this.isComponentReady || Object.keys(this.storyMap).length === 0) {
        return;
      }
      if (clearExpanded) {
        this.isExpanded = false;
        this.expandedContentHeight = null;
        this.clearPendingExpandedMeasurements();
      }
      this.refreshIndex += 1;
      this.$nextTick(() => {
        this.setupContent();
        this.$nextTick(() => {
          this.setupContentResizeObserver();
          this.checkOverflow();
          this.emitPodcastLayoutRefresh();
        });
      });
    },
    onSelect({ item, stop = false }) {
      if (typeof item === 'undefined') return Promise.resolve(1);
      const disableNavBarAutomaticProgression = get(item, 'disableNavBarAutomaticProgression', false);
      if (stop || disableNavBarAutomaticProgression) {
        this.stopNavRotation();
        this.selectedManually = true;
      }

      this.isTransitioning = true;
      return this.getStory({ index: item.index })
        .then((data) => {
          const timeout = 0;
          if (stop) {
            window.scrollTo(0, 0);
          }
          this.selected = item;
          setTimeout(() => {
            this.isTransitioning = false;
          }, timeout);
        })
    },
    stopNavRotation() {
      if (interval !== null) {
        clearInterval(interval);
      }
    },
    startNavRotation() {
      this.stopNavRotation();
      interval = setInterval(() => {
        const currentIndex = this.selected.index;
        const nextIndex = currentIndex === (this.storiesNav.length - 1) ? 0 : currentIndex + 1;
        this.onSelect({ item: this.storiesNav[nextIndex] });
      }, STORY_SWITCH_INTERVAL);
    },
    resolveInitialStoryItem(data) {
      if (this.$route.query.story) {
        const indexToSelect = parseInt(this.$route.query.story, 10);
        const selectedItem = data[indexToSelect - 1];

        if (selectedItem) {
          this.activeTab = `story-${selectedItem.index}`;
          return selectedItem;
        }
      }

      const defaultItem = data[0];
      if (defaultItem) {
        this.activeTab = `story-${defaultItem.index}`;
      }

      return defaultItem;
    },
    start() {
      this.stopNavRotation();
      this.isLoading = true;
      this.isComponentReady = false;
      this.getStoriesNav()
        .then((data) => {
          if (data.length === 0) {
            this.isLoading = false;
            this.isComponentReady = true;
            return Promise.resolve(1);
          }

          const item = this.resolveInitialStoryItem(data);
          if (!item) {
            this.isLoading = false;
            this.isComponentReady = true;
            return Promise.resolve(1);
          }

          return this.getStoriesList({ indexes: data.map(i => i.index), cache: true })
            .then(() => {
              return this.onSelect({ item })
                .then(() => {
                  this.isLoading = false;
                  this.setupContent();

                  setTimeout(() => {
                    this.checkOverflow();
                  }, 0);

                  const disableNavBarAutomaticProgression = get(item || {}, 'disableNavBarAutomaticProgression', false);
                  if (!this.currentPopup && !disableNavBarAutomaticProgression) {
                    this.startNavRotation();
                  }
                });
            });
        })
        .catch((e) => {
          console.error(e);
          this.isLoading = false;
          this.isComponentReady = true;
        });
    },
    checkScrollButtons() {
      const tabsList = this.$refs.tabsList;
      if (tabsList) {
        const tolerance = 25;
        this.canScrollLeft = tabsList.scrollLeft > tolerance;
        this.canScrollRight = tabsList.scrollLeft < (tabsList.scrollWidth - tabsList.clientWidth - tolerance);
      }
    },
    checkOverflow() {
      const tabsList = this.$refs.tabsList;
      if (tabsList) {
        this.showNavigationArrows = tabsList.scrollWidth > tabsList.clientWidth;
        setTimeout(() => {
          this.checkScrollButtons();
        }, 250)
      }
    },
    scrollLeft() {
      const tabsList = this.$refs.tabsList;
      if (tabsList) {
        tabsList.scrollBy({
          left: -150, // Scroll left by 150px
          behavior: 'smooth'
        });
        setTimeout(() => {
          this.checkScrollButtons();
        }, 250)
      }
    },
    scrollRight() {
      const tabsList = this.$refs.tabsList;
      if (tabsList) {
        tabsList.scrollBy({
          left: 150, // Scroll right by 150px
          behavior: 'smooth'
        });
        setTimeout(() => {
          this.checkScrollButtons();
        }, 250)
      }
    },
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
      if (this.isExpanded) {
        this.scheduleExpandedHeightRefresh();
      } else {
        this.expandedContentHeight = null;
        this.clearPendingExpandedMeasurements();
      }
      this.stopNavRotation();
      this.selectedManually = true;
      this.$nextTick(() => {
        this.emitPodcastLayoutRefresh();
      });
    },
    checkContentOverflow() {
      const content = this.$refs.content;
      if (content) {
        const collapsedContentHeight = this.measureCollapsedContentHeight() || content.scrollHeight || 0;
        this.isContentOverflowing = collapsedContentHeight > (CONTENT_COLLAPSED_HEIGHT + CONTENT_OVERFLOW_TOLERANCE);
        if (this.isExpanded) {
          const expandedContentHeight = this.measureExpandedContentHeight();
          if (expandedContentHeight) {
            this.expandedContentHeight = expandedContentHeight;
          }
        }
      }
    },
  },
  created() {
    this.start();
  },
  watch: {
    currentStoryIndex(val) {
      this.isExpanded = false;
      this.expandedContentHeight = null;
      this.clearPendingExpandedMeasurements();
      this.$nextTick(() => {
        this.installActiveStoryCharts();
        this.setupContentResizeObserver();
        this.checkContentOverflow();
        this.emitPodcastLayoutRefresh();
        setTimeout(() => this.emitPodcastLayoutRefresh(), 200);
      });
      const activeTab = `story-${val}`;
      if (activeTab !== this.activeTab) {
        this.activeTab = activeTab;
      }
    },
    isLayoutDesktop(value, oldValue) {
      if (value === oldValue) {
        return;
      }
      this.$nextTick(() => {
        this.refreshLayoutContent({ clearExpanded: true });
      });
    },
    currentPopup(value) {
      if (this.selectedManually) return;
      if (value) {
        this.stopNavRotation();
      } else {
        this.startNavRotation();
      }
    },
    currentSmallPopup(value) {
      if (this.selectedManually) return;
      if (value) {
        this.stopNavRotation();
      } else {
        this.startNavRotation();
      }
    },
  },
  beforeDestroy() {
    this.stopNavRotation();
    this.clearPendingExpandedMeasurements();
    this.teardownContentResizeObserver();
    window.removeEventListener('resize', this.handleWindowResize);
    window.removeEventListener(PODCAST_RESIZE_EVENT, this.handleEmbeddedResizeEvent);
  },
  mounted() {
    window.addEventListener('resize', this.handleWindowResize);
    window.addEventListener(PODCAST_RESIZE_EVENT, this.handleEmbeddedResizeEvent);
  },
};



// WEBPACK FOOTER //
// ./src/components/cms/preview-carousel/preview-carousel.js